import { createSlice } from '@reduxjs/toolkit';
import { ACCESSIBILITY_COLOR_MODES } from 'config/accessibilityTheme.config';
import {
  DEFAULT_ACCESSIBILITY_OPTIONS,
  DEFAULT_DISPLAY_OPTIONS,
  DISPLAY_THEME_STORAGE_KEY,
  MAP_OPTIONS_STORAGE_KEY,
  CHART_OPTIONS_STORAGE_KEY,
  ACCESSIBILITY_OPTIONS_STORAGE_KEY,
  NAVIGATION_STYLE_STORAGE_KEY,
} from 'store/config/displayOptions.config';
import {
  getDisplayStyleForAccessibilityColorMode,
  normalizeAccessibilityOptions,
  normalizeDisplayOptions,
  clearStoredDisplayOptions,
  persistDisplayOptions,
  normalizeNavigationStyle,
  persistOptions,
  readStoredDisplayOptions,
  readStoredNavigationStyle,
} from 'store/utils/displayOptions.utils';
import {
  selectAccessibilityOptions,
  selectChartOptions,
  selectDisplayTheme,
  selectMapOptions,
  selectPersistableDisplayOptions,
  selectResolvedDisplayTheme,
  selectResolvedThemeTokens,
} from 'store/selectors/DisplayOptions/displayOptionsSelectors';
import { normalizeChartOptions } from 'utils/charts/chartStyle.utils';
import { normalizeMapOptions } from 'utils/maps/mapTile.utils';
import {
  DEFAULT_DISPLAY_THEME,
  normalizeAccessibilityColorMode,
  normalizeDisplayTheme,
} from 'utils/themes/theme.utils';

export {
  DEFAULT_ACCESSIBILITY_OPTIONS,
  DEFAULT_DISPLAY_OPTIONS,
  DEFAULT_DISPLAY_THEME,
  normalizeAccessibilityOptions,
  normalizeDisplayTheme,
  selectAccessibilityOptions,
  selectChartOptions,
  selectDisplayTheme,
  selectMapOptions,
  selectPersistableDisplayOptions,
  selectResolvedDisplayTheme,
  selectResolvedThemeTokens,
};

// The navigation style is not part of the profile display options: hydrating, resetting or
// clearing those leaves it as the person set it on this device.
const initialState = {
  ...readStoredDisplayOptions(),
  navigationStyle: readStoredNavigationStyle(),
};

const displayOptionsSlice = createSlice({
  name: 'displayOptions',
  initialState,
  reducers: {
    setMapOptions(state, action) {
      state.mapOptions = normalizeMapOptions(action.payload);
      persistOptions(MAP_OPTIONS_STORAGE_KEY, state.mapOptions);
    },
    setMapStyle(state, action) {
      state.mapOptions = normalizeMapOptions({
        ...state.mapOptions,
        mapStyle: action.payload,
      });
      persistOptions(MAP_OPTIONS_STORAGE_KEY, state.mapOptions);
    },
    setChartOptions(state, action) {
      state.chartOptions = normalizeChartOptions(action.payload);
      persistOptions(CHART_OPTIONS_STORAGE_KEY, state.chartOptions);
    },
    setChartStyle(state, action) {
      state.chartOptions = normalizeChartOptions({
        ...state.chartOptions,
        chartStyle: action.payload,
      });
      persistOptions(CHART_OPTIONS_STORAGE_KEY, state.chartOptions);
    },
    setNavigationStyle(state, action) {
      state.navigationStyle = normalizeNavigationStyle(action.payload);
      persistOptions(NAVIGATION_STYLE_STORAGE_KEY, state.navigationStyle);
    },
    setDisplayTheme(state, action) {
      state.displayTheme = normalizeDisplayTheme(action.payload);
      persistOptions(DISPLAY_THEME_STORAGE_KEY, state.displayTheme);
    },
    hydrateDisplayOptions(state, action) {
      const options = normalizeDisplayOptions({
        ...state,
        ...(action.payload || {}),
      });

      state.displayTheme = options.displayTheme;
      state.mapOptions = options.mapOptions;
      state.chartOptions = options.chartOptions;
      state.accessibilityOptions = options.accessibilityOptions;

      persistDisplayOptions(options);
    },
    resetDisplayOptions(state) {
      const options = normalizeDisplayOptions(DEFAULT_DISPLAY_OPTIONS);

      state.displayTheme = options.displayTheme;
      state.mapOptions = options.mapOptions;
      state.chartOptions = options.chartOptions;
      state.accessibilityOptions = options.accessibilityOptions;

      persistDisplayOptions(options);
    },
    clearDisplayOptions(state) {
      const options = normalizeDisplayOptions(DEFAULT_DISPLAY_OPTIONS);

      state.displayTheme = options.displayTheme;
      state.mapOptions = options.mapOptions;
      state.chartOptions = options.chartOptions;
      state.accessibilityOptions = options.accessibilityOptions;

      clearStoredDisplayOptions();
    },
    setAccessibilityColorMode(state, action) {
      const colorMode = normalizeAccessibilityColorMode(action.payload);
      const displayStyle = getDisplayStyleForAccessibilityColorMode(colorMode);

      state.accessibilityOptions = normalizeAccessibilityOptions({ colorMode });
      state.mapOptions = normalizeMapOptions({
        ...state.mapOptions,
        mapStyle: displayStyle,
      });
      state.chartOptions = normalizeChartOptions({
        ...state.chartOptions,
        chartStyle: displayStyle,
      });

      persistOptions(ACCESSIBILITY_OPTIONS_STORAGE_KEY, state.accessibilityOptions);
      persistOptions(MAP_OPTIONS_STORAGE_KEY, state.mapOptions);
      persistOptions(CHART_OPTIONS_STORAGE_KEY, state.chartOptions);
    },
    toggleMonochrome(state, action) {
      const currentColorMode = normalizeAccessibilityOptions(state.accessibilityOptions).colorMode;
      const enable = action.payload ?? currentColorMode !== ACCESSIBILITY_COLOR_MODES.MONOCHROME;
      const colorMode = enable
        ? ACCESSIBILITY_COLOR_MODES.MONOCHROME
        : ACCESSIBILITY_COLOR_MODES.DEFAULT;
      const displayStyle = getDisplayStyleForAccessibilityColorMode(colorMode);

      state.accessibilityOptions = normalizeAccessibilityOptions({ colorMode });
      state.mapOptions = normalizeMapOptions({
        ...state.mapOptions,
        mapStyle: displayStyle,
      });
      state.chartOptions = normalizeChartOptions({
        ...state.chartOptions,
        chartStyle: displayStyle,
      });

      persistOptions(ACCESSIBILITY_OPTIONS_STORAGE_KEY, state.accessibilityOptions);
      persistOptions(MAP_OPTIONS_STORAGE_KEY, state.mapOptions);
      persistOptions(CHART_OPTIONS_STORAGE_KEY, state.chartOptions);
    },
    toggleHighContrast(state, action) {
      const currentColorMode = normalizeAccessibilityOptions(state.accessibilityOptions).colorMode;
      const enable = action.payload ?? currentColorMode !== ACCESSIBILITY_COLOR_MODES.HIGH_CONTRAST;
      const colorMode = enable
        ? ACCESSIBILITY_COLOR_MODES.HIGH_CONTRAST
        : ACCESSIBILITY_COLOR_MODES.DEFAULT;
      const displayStyle = getDisplayStyleForAccessibilityColorMode(colorMode);

      state.accessibilityOptions = normalizeAccessibilityOptions({ colorMode });
      state.mapOptions = normalizeMapOptions({
        ...state.mapOptions,
        mapStyle: displayStyle,
      });
      state.chartOptions = normalizeChartOptions({
        ...state.chartOptions,
        chartStyle: displayStyle,
      });

      persistOptions(ACCESSIBILITY_OPTIONS_STORAGE_KEY, state.accessibilityOptions);
      persistOptions(MAP_OPTIONS_STORAGE_KEY, state.mapOptions);
      persistOptions(CHART_OPTIONS_STORAGE_KEY, state.chartOptions);
    },
  },
});

export const {
  setMapOptions,
  setMapStyle,
  setChartOptions,
  setChartStyle,
  setDisplayTheme,
  hydrateDisplayOptions,
  resetDisplayOptions,
  clearDisplayOptions,
  setAccessibilityColorMode,
  toggleMonochrome,
  toggleHighContrast,
  setNavigationStyle,
} = displayOptionsSlice.actions;

export default displayOptionsSlice.reducer;
